// Realtime + health context. Exposes the SSE stream state, the moment of the last
// confirmed data and an event subscription. The API health is polled from /v1/health —
// the UI never claims "connected"/"operational" without current evidence (RF-UI-03).
import type { EventEnvelope } from "@maestros/contracts";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getHealth } from "../api/endpoints.ts";
import type { HealthResponse } from "../api/types.ts";
import { EventStream, type StreamStatus } from "./sse.ts";

type Listener = (event: EventEnvelope) => void;

export interface RealtimeState {
  status: StreamStatus;
  /** When data was last confirmed fresh (stream open, event or heartbeat). */
  lastConfirmedAt: Date | null;
  /** When the stream stopped being live; null while live. */
  staleSince: Date | null;
  retryInMs: number | null;
  health: HealthResponse | null;
  healthError: boolean;
  healthCheckedAt: Date | null;
  subscribe: (listener: Listener) => () => void;
  reconnect: () => void;
  refreshHealth: () => void;
}

const RealtimeContext = createContext<RealtimeState | null>(null);

const HEALTH_INTERVAL_MS = 30_000;

export function RealtimeProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [lastConfirmedAt, setLastConfirmedAt] = useState<Date | null>(null);
  const [staleSince, setStaleSince] = useState<Date | null>(null);
  const [retryInMs, setRetryInMs] = useState<number | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState(false);
  const [healthCheckedAt, setHealthCheckedAt] = useState<Date | null>(null);
  const listeners = useRef(new Set<Listener>());
  const stream = useRef<EventStream | null>(null);
  const lastActivityWrite = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const s = new EventStream({
      onEvent: (e) => {
        for (const l of listeners.current) {
          try {
            l(e);
          } catch (err) {
            console.error("realtime listener failed", err);
          }
        }
      },
      onStatus: (next, info) => {
        setStatus(next);
        setRetryInMs(info.retryInMs);
        if (next === "live") setStaleSince(null);
        else setStaleSince((prev) => prev ?? new Date());
      },
      onActivity: () => {
        // Throttle re-renders caused by heartbeats.
        const now = Date.now();
        if (now - lastActivityWrite.current > 1000) {
          lastActivityWrite.current = now;
          setLastConfirmedAt(new Date(now));
        }
      },
    });
    stream.current = s;
    s.start();
    const online = () => s.reconnectNow();
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("online", online);
      s.stop();
      stream.current = null;
    };
  }, [enabled]);

  const refreshHealth = useCallback(() => {
    getHealth()
      .then((h) => {
        setHealth(h);
        setHealthError(false);
      })
      .catch(() => setHealthError(true))
      .finally(() => setHealthCheckedAt(new Date()));
  }, []);

  useEffect(() => {
    refreshHealth();
    const id = window.setInterval(refreshHealth, HEALTH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refreshHealth]);

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const reconnect = useCallback(() => stream.current?.reconnectNow(), []);

  const value = useMemo<RealtimeState>(
    () => ({
      status: enabled ? status : "offline",
      lastConfirmedAt,
      staleSince: enabled ? staleSince : null,
      retryInMs,
      health,
      healthError,
      healthCheckedAt,
      subscribe,
      reconnect,
      refreshHealth,
    }),
    [enabled, status, lastConfirmedAt, staleSince, retryInMs, health, healthError, healthCheckedAt, subscribe, reconnect, refreshHealth],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeState {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtime must be used inside RealtimeProvider");
  return ctx;
}

/** Subscribe to events, optionally filtered by project; the callback may change freely. */
export function useRealtimeEvents(handler: Listener, projectId?: string | null): void {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(
    () =>
      subscribe((e) => {
        if (projectId && e.project_id !== projectId) return;
        ref.current(e);
      }),
    [subscribe, projectId],
  );
}
